import { useMemo, useState } from 'react'
import { Card, Col, Image, Row, Table } from 'react-bootstrap'
import Cookies from 'js-cookie'

/**
 * Marksheet print layout + sample data — mirrors the printable block in ExamMarksheet.js
 * (header, watermark, student profile row, subjects × exams table, totals, signatures).
 */

const calculateOverallTotals = (rows) => {
  let totalObtained = 0
  let totalMax = 0
  rows.forEach((entry) => {
    const marks = parseFloat(entry.marks)
    const totalMarks = entry.exam.total_marks
    totalObtained += marks
    totalMax += totalMarks
  })
  const percentage = totalMax > 0 ? ((totalObtained / totalMax) * 100).toFixed(2) : '0.00'
  return { totalObtained, totalMax, percentage }
}

const processSubjectData = (rows) => {
  const subjectMap = {}
  rows.forEach((entry) => {
    const subjectId = entry.subject_id
    const subjectName = entry.subject.subject_name
    const examName = entry.exam.exam_name
    const marks = parseFloat(entry.marks)
    const status = entry.status
    const totalMarks = entry.exam.total_marks

    if (!subjectMap[subjectId]) {
      subjectMap[subjectId] = {
        subjectId,
        subjectName,
        exams: {},
        totalMarksObtained: 0,
        totalExamCount: 0,
        statusList: [],
        maxTotalMarks: 0,
      }
    }

    subjectMap[subjectId].exams[examName] = {
      marks,
      totalMarks,
    }
    subjectMap[subjectId].totalMarksObtained += marks
    subjectMap[subjectId].totalExamCount += 1
    subjectMap[subjectId].statusList.push(status)
    subjectMap[subjectId].maxTotalMarks = totalMarks
  })

  return Object.values(subjectMap).map((subject) => {
    const average = (subject.totalMarksObtained / subject.totalExamCount).toFixed(2)
    const finalStatus = subject.statusList.includes('fail') ? 'Fail' : 'Pass'
    return {
      subjectId: subject.subjectId,
      subjectName: subject.subjectName,
      exams: subject.exams,
      average,
      finalStatus,
    }
  })
}

const normalizeMarksheetRows = (rows) => {
  if (!Array.isArray(rows)) return []
  return rows
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const exam = row.exam || {}
      const subject = row.subject || {}
      const student = row.student || {}
      const course = row.course || {}
      const marksValue = row.marks ?? row.obtained_marks ?? 0
      return {
        ...row,
        subject_id: row.subject_id ?? subject.id ?? subject.subject_id,
        exam_id: row.exam_id ?? exam.id ?? exam.exam_id,
        marks: String(marksValue),
        status: (row.status || '').toString().toLowerCase(),
        exam: {
          ...exam,
          exam_name: exam.exam_name || exam.name || 'Exam',
          total_marks: Number(exam.total_marks ?? exam.max_marks ?? 0),
          session: exam.session || exam.academic_session || '',
        },
        subject: {
          ...subject,
          subject_name: subject.subject_name || subject.name || 'Subject',
        },
        student: {
          ...student,
        },
        course: {
          ...course,
          name: course.name || course.course_name || '',
        },
      }
    })
    .filter(Boolean)
}

export const buildExamMarksheetPreviewData = () => {
  const course = { course_name: 'Sample Course', id: 101 }
  const student = {
    admission_number: 'ADM-2026-001',
    id: 501,
    name: 'Student Name',
    father_name: 'Father Name',
    mother_name: 'Mother Name',
    phone: '9876543210',
    dob: '2010-06-15',
    student_image: '/studentprofile.jpg',
  }
  const mk = (subjectId, subjectName, examId, examName, totalMarks, marks, status) => ({
    subject_id: subjectId,
    subject: { subject_name: subjectName },
    exam_id: examId,
    exam: {
      exam_name: examName,
      total_marks: totalMarks,
      session: '2025-2026',
    },
    course,
    student,
    marks: String(marks),
    status,
  })
  return [
    mk(1, 'Mathematics', 1, 'Mid Term', 100, 88, 'pass'),
    mk(1, 'Mathematics', 2, 'Final Term', 100, 92, 'pass'),
    mk(2, 'English', 1, 'Mid Term', 100, 75, 'pass'),
    mk(2, 'English', 2, 'Final Term', 100, 80, 'pass'),
    mk(3, 'Science', 1, 'Mid Term', 100, 70, 'pass'),
    mk(3, 'Science', 2, 'Final Term', 100, 78, 'pass'),
  ]
}

/* Screen + print styles subset from ExamMarksheet.js (.print-card, headers, table) */
const examMarksheetPrintStyles = `
  .filter-card-container {
    overflow: visible !important;
    position: relative !important;
    z-index: 1 !important;
  }
  
  .filter-card-container .card {
    overflow: visible !important;
    position: relative !important;
  }
  
  .filter-card-container .card-body {
    overflow: visible !important;
    position: relative !important;
  }
  
  .filter-card-container .row {
    overflow: visible !important;
    position: relative !important;
  }
  
  .filter-card-container .col {
    overflow: visible !important;
    position: relative !important;
  }

  .rbt-menu {
    z-index: 9999 !important;
    position: absolute !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
  
  .rbt-input-wrapper {
    position: relative !important;
    z-index: 1 !important;
  }
  
  .rbt-input-hint {
    z-index: 1 !important;
  }
  
  .rbt-input-main {
    z-index: 2 !important;
  }
  
  .rbt-menu.show {
    z-index: 9999 !important;
  }
  
  /* Ensure Typeahead dropdown appears above everything */
  .rbt-menu.dropdown-menu {
    z-index: 9999 !important;
    position: absolute !important;
  }

  /* ✅ Base Card */
.print-card {
  position: relative;
  background-color: white;
  overflow: hidden;
  border: none !important;
}

/* ✅ Watermark inside card (visible in UI) */
.print-card .watermark-logo-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: auto;
  opacity: 0.08; /* very soft — text/tables stay primary */
  z-index: 0;
  pointer-events: none;
}

.print-card .watermark-logo {
  width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center;
}

/* ✅ Content above watermark */
.print-card > *:not(.watermark-logo-wrapper):not(.watermark-logo),
.print-card table,
.print-card h2,
.print-card h4,
.print-card h5,
.print-card p {
  position: relative;
  z-index: 2;
}

/* ✅ Transparent background for table */
.print-card table,
.print-card th,
.print-card td {
  background-color: transparent !important;
  z-index: 1 !important;
}


/* ✅ PRINT STYLES
   Scoped to .exam-marksheet-print-root so TemplateBasedCertificate (and modals) are not
   broken by the old global sidebar/width hacks — those remain only in ExamMarksheet.js
   for the /ExamMarksheet route (#print_page + ancestor compensation). */
@media print {
  body,
  html {
    margin: 0 !important;
    padding: 0 !important;
    font-size: 13px !important;
    overflow: hidden !important;
  }

  /* Hide sidebar and navigation completely */
  aside, nav, .sidebar, [class*="sidebar"], [class*="Sidebar"],
  [class*="Sidenavbar"], [class*="sidenavbar"] {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #print_page {
    margin-top: 0 !important;
  }

  .exam-marksheet-print-root,
  .exam-marksheet-print-root * {
    font-size: inherit !important;
  }

  /* Full usable width; no 176% / negative offset (that pair assumed global container hacks) */
  .exam-marksheet-print-root .print-card {
    width: 100% !important;
    max-width: 100% !important;
    left: 0 !important;
    position: relative !important;
    transform: none !important;
    box-sizing: border-box !important;
  }

  .no-print,
  .print-buttons {
    display: none !important;
  }

  /* ✅ Full-page watermark for print */
  .exam-marksheet-print-root .print-card .watermark-logo-wrapper {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    transform: none !important;
    opacity: 0.08 !important;
    z-index: 0 !important;
  }

  .exam-marksheet-print-root .print-card .watermark-logo {
    width: 100% !important;
    height: 100% !important;
    object-fit: contain !important;
  }

  .exam-marksheet-print-root .print-card > *:not(.watermark-logo-wrapper):not(.watermark-logo) {
    position: relative !important;
    z-index: 2 !important;
  }

  /* ✅ Profile Layout Adjustments */
  .profile-print {
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-between !important;
    align-items: center !important;
    flex-wrap: nowrap !important;
    gap: 0 !important;
    margin-top: -0.8rem !important;
    margin-bottom: -0.2rem !important;
    line-height: -3 !important;
  }

  .profile-print .col-md-9,
  .profile-print > .col-md-8 {
    width: 75% !important;
    flex: 0 0 75% !important;
    max-width: 75% !important;
  }

  .profile-print .col-md-3,
  .profile-print > .col-md-3 {
    width: 25% !important;
    flex: 0 0 25% !important;
    max-width: 25% !important;
    text-align: center !important;
    align-self: flex-start !important;
  }

  .profile-print .col-md-8 .row {
    display: flex !important;
    flex-direction: row !important;
  }

  .profile-print .col-md-8 .col-md-6 {
    width: 50% !important;
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }

  .profile-print img {
    width: 130px !important;
    height: 130px !important;
    object-fit: cover !important;
    border: 0.5px solid #333 !important;
    display: block;
    margin: auto;
  }

  .table-head-print th {
    color: dark !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    font-size: 12px !important;
    padding: 6px 6px !important;
  }

  .table-body-print td {
    font-size: 12px !important;
    padding: 6px 6px !important;
  }

  .exam-marksheet-print-root .print-card .institute-header-text h2,
  .exam-marksheet-print-root .print-card .institute-header-text h4 {
    font-size: 14px !important;
  }

  .print-logo {
    width: 90px !important;
    height: 90px !important;
    border-radius: 50% !important;
    object-fit: cover !important;
  }

  .nielit-logo {
    width: 80px !important;
    height: 80px !important;
    margin-top: 3rem !important;
  }

  .img-nct {
    width: 50px !important;
    height: 50px !important;
  }

  .exam-marksheet-print-root .print-card .sub-header h4 {
    font-size: 14px !important;
    margin-top: -0.5rem !important;
  }

  .exam-marksheet-print-root .print-card .sub-header h5 {
    margin-top: -0.5rem !important;
  }

  .exam-marksheet-print-root .print-card .sub-header p {
    margin-top: -0.5rem !important;
    margin-bottom: -0.8rem !important;
  }

  .exam-marksheet-print-root .print-card .powered-by-footer {
    margin-top: -0.7rem !important;
  }

  .exam-marksheet-print-root .print-card .powered-by-footer .powered {
    margin-top: 1rem !important;
  }
}

.institute-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.institute-header-text {
    flex-grow: 1;
    text-align: center;
}

.institute-header > div:first-child,
.institute-header > div:last-child {
    width: 120px;
}

@media print {
  .table-footer-print {
    position: fixed !important;
    bottom: 2rem !important;
    left: 0 !important;
    width: 100% !important;
    z-index: 999 !important;
  }
}


/* ✅ PDF Style Fixes */
.pdf-style {
  font-size: 11px !important;
  padding: 5mm 8mm;
}

.pdf-style * {
  font-size: 11px !important;
  line-height: 1.4 !important;
}

.pdf-style h1 {
  font-size: 18px !important;
}

.pdf-style h4,
.pdf-style h5 {
  font-size: 14px !important;
}

.pdf-style p,
.pdf-style td,
.pdf-style th,
.pdf-style small {
  font-size: 11px !important;
}

.pdf-style .no-print {
  display: none !important;
}

.pdf-style .card {
  border: none !important;
  box-shadow: none !important;
}

.pdf-style .sub-header h4,
.pdf-style .sub-header h5,
.pdf-style .sub-header p {
  margin-left: auto !important;
  margin-right: auto !important;
  text-align: center !important;
}

.pdf-style .marksheet-content {
  position: relative;
  z-index: 1;
}

.pdf-style .watermark-logo-wrapper {
  width: 100% !important;
  max-width: 100% !important;
  opacity: 0.08 !important;
}

/* ============================
   PDF DOWNLOAD ONLY
   ============================ */

   .download-style  * {
    font-size: 14px !important;
  }

/* Force A4 page size for download */
.download-style .print-card-download {
  width: 100% !important;
  margin: 0 auto !important;
  box-shadow: none !important;
  border: none !important;
  height: 1123px !important; /* A4 height in pixels (297mm) */
  min-height: 1123px !important;
  max-height: 1123px !important;
  margin-top: -1rem !important;
  position: relative !important;
  page-break-inside: avoid !important;
}


 .download-style .profile-print img {
    margin-left:5rem !important;
  }

/* Container to push content */
.download-style .marksheet-content-wrapper {
  min-height: 1000px !important;
  position: relative !important;
}

/* Footer always at bottom */
.download-style .table-footer-download {
  position: absolute !important;
  bottom: -90px !important;
  left: 20px !important;
  right: 20px !important;
  margin-top: auto !important;
  z-index: 1000 !important;
   column-gap: 5rem !important;
  // padding: 10px 0 !important;
}

/* Table container with minimum height */
.download-style .table-container {
  min-height: 600px !important; /* Adjust based on your typical table height */
  position: relative !important;
}

/* Spacer to push footer down */
.footer-spacer {
  flex-grow: 1 !important;
  visibility: hidden !important;
}

/* Ensure table takes available space */
.download-style .print-card-download > .mt-3 {
  min-height: 500px !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Make sure the card has relative positioning */
.download-style .print-card {
  position: relative !important;
  min-height: 280mm !important; /* Adjust based on your content */
}

.download-style .print-logo {
  border-radius: 50% !important;
  object-fit: cover !important;
  clip-path: circle(50%) !important;
}


.download-style th,
.download-style td {
  font-size: 11px !important;
  background-color: transparent !important;
  border-color: #000 !important;
}
 

.download-style .no-print{
display: none !important;
}

.overall-totals-text{
  display: flex !important;
  justify-content: center !important;
}

.download-style * {
  position: relative;
}
`

const ExamMarksheetPrint = ({
  logo,
  instituteName,
  instituteAddress = Cookies.get('address'),
  institutePincode = Cookies.get('pincode'),
  data: dataProp,
}) => {
  const [logoError, setLogoError] = useState(false)
  const cookieInstituteId = String(Cookies.get('institute_id') || '').trim()
  const hideNctBranding = cookieInstituteId != '12'

  const normalizedData = useMemo(() => normalizeMarksheetRows(dataProp), [dataProp])
  const data = useMemo(() => {
    if (normalizedData.length > 0) return normalizedData
    // Keep preview fallback only when no external data is passed.
    return dataProp === undefined ? buildExamMarksheetPreviewData() : []
  }, [dataProp, normalizedData])
  const firstRow = data[0]

  const processedSubjects = useMemo(() => processSubjectData(data), [data])
  const overallTotals = useMemo(() => calculateOverallTotals(data), [data])

  const uniqueExams = useMemo(
    () => Array.from(new Map(data.map((item) => [item.exam_id, item.exam.exam_name])).entries()),
    [data],
  )
  const examNames = useMemo(() => uniqueExams.map(([, name]) => name), [uniqueExams])

  const examTotalMarksMap = useMemo(() => {
    const m = new Map()
    data.forEach((item) => {
      m.set(item.exam.exam_name, item.exam.total_marks)
    })
    return m
  }, [data])

  const img = data[0]?.student
  const marksheetNumber =
    firstRow?.student?.marksheet_number || `MS-${firstRow?.course?.id || 'NA'}-${firstRow?.student?.id || 'NA'}`

  return (
    <>
      <style>{examMarksheetPrintStyles}</style>
      <div className="exam-marksheet-print-root position-relative watermark-container">
        <div className="watermark-content">
          <Card className="p-4 shadow-sm print-card print-card-download">
            <div className="watermark-logo-wrapper">
              <img
                src={Cookies.get('institute_logo') || '/markWatermark.png'}
                alt=""
                className="watermark-logo"
                onError={(e) => {
                  e.currentTarget.onerror = null
                  e.currentTarget.src = '/markWatermark.png'
                }}
              />
            </div>
            <div className="d-flex justify-content-between align-items-center institute-header">
              <div className="me-5">
                {!logoError ? (
                  <Image
                    src={logo || '/logo.svg'}
                    alt="Logo"
                    className="print-logo"
                    style={{ width: '90px', height: '90px', borderRadius: '50%' }}
                    onError={() => setLogoError(true)}
                  />
                ) : (
                  <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#e5e7eb' }} />
                )}
              </div>

              <div className="institute-header-text" style={{ color: 'blue' }}>
                <h2 className="fw-bold mb-2">{instituteName || 'Institute Name'}</h2>
                <h4 className="fw-bold">
                  {instituteAddress} - {institutePincode}
                </h4>
                {!hideNctBranding && (
                  <p className="powered" style={{ fontStyle: 'italic', fontSize: '14px', fontWeight: '600' }}>
                    Powered by <strong>Tera Tujko Arpan (ISO 9001 : 2015)</strong>
                  </p>
                )}
              </div>

              <div className="text-center">
                {!hideNctBranding && (
                  <>
                    <img
                      src="/nct_logo2.png"
                      alt="NCT Logo"
                      className="img-nct"
                      style={{ width: '70px', height: '70px' }}
                    />
                    <div className="gvt" style={{ fontSize: '10px', fontWeight: 'bold', marginTop: '4px' }}>
                      GOVERNMENT OF NCT OF DELHI
                    </div>
                  </>
                )}
              </div>
            </div>

            <hr />
            <div className="sub-header d-flex align-items-center justify-content-between position-relative">
              <h4 className="text fw-bold text-uppercase w-100 text-center m-0">{firstRow?.course?.name}</h4>
            </div>

            <div className="text-center">
              <h5 className="fw-semibold mt-0">Student Result</h5>
              <p className="text-muted fw-semibold">Session : {firstRow?.exam?.session}</p>
            </div>
            <hr style={{ marginTop: '1rem' }} />

            <Row className="text-start align-items-center profile-print">
              <Col xs={12} md={8}>
                <Row>
                  <Col xs={12} md={6}>
                    <p>
                      <strong>Admission Number : {firstRow?.student?.admission_number}</strong>
                    </p>
                    <p>
                      <strong>Marksheet Number : {marksheetNumber}</strong>
                    </p>
                    <p>
                      <strong>Father&apos;s Name : {firstRow?.student?.father_name}</strong>
                    </p>
                    <p>
                      <strong>Phone : {firstRow?.student?.phone}</strong>
                    </p>
                  </Col>
                  <Col xs={12} md={6}>
                    <p>
                      <strong>NAME OF STUDENT : {firstRow?.student?.name}</strong>
                    </p>
                    <p>
                      <strong>
                        Date of Birth :{' '}
                        {firstRow?.student?.dob
                          ? new Date(firstRow.student.dob).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })
                          : ''}
                      </strong>
                    </p>
                    <p>
                      <strong>Mother&apos;s Name : {firstRow?.student?.mother_name}</strong>
                    </p>
                  </Col>
                </Row>
              </Col>
              <Col xs={12} md={3} className="text-center mt-md-0">
                <Image
                  crossOrigin="anonymous"
                  src={img?.student_image || '/studentprofile.jpg'}
                  style={{ width: '120px', height: '120px', objectFit: 'cover', border: '1px solid #333' }}
                  className="mt-2"
                  alt="Student"
                />
              </Col>
            </Row>
            <hr />
            <div className="mt-3">
              {data.length > 0 ? (
                <Table striped bordered hover className="text-center marksheet-table">
                  <thead className="text-dark table-head-print" style={{ backgroundColor: 'white' }}>
                    <tr>
                      <th>S. No.</th>
                      <th>Subject</th>
                      {uniqueExams.map(([examId, examName]) => (
                        <th key={examId}>{examName}</th>
                      ))}
                      <th>Cumulative</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody className="table-body-print" style={{ fontWeight: 'bold' }}>
                    {processedSubjects.map((subject, index) => (
                      <tr key={subject.subjectId}>
                        <td>{index + 1}</td>
                        <td>{subject.subjectName}</td>
                        {examNames.map((exam, i) => {
                          const totalMarks = examTotalMarksMap.get(exam)
                          const subjectExam = subject.exams[exam]
                          return (
                            <td key={i}>
                              {subjectExam ? (
                                <div>
                                  <div className="small fw-bold">
                                    {subjectExam.marks} | {totalMarks}
                                  </div>
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                          )
                        })}
                        <td>{subject.average}</td>
                        <td className={subject.finalStatus === 'Pass' ? 'text-success' : 'text-danger'}>
                          {subject.finalStatus}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-3 fw-semibold">No marks data available.</div>
              )}

              <div className="fw-bold overall-totals-text">
                <p>
                  Total Marks Obtained: {overallTotals.totalObtained} / {overallTotals.totalMax}
                  &nbsp;&nbsp; | &nbsp;&nbsp; Overall Percentage: {overallTotals.percentage}%
                </p>
              </div>
            </div>

            <div>
              <div className="justify-content-between d-flex mt-5 table-footer-print table-footer-download">
                <p className="fw-bold">
                  Verified By___________________________________
                </p>
                <p className="fw-bold">
                  Authorized
                  Signature_______________________________________
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </>
  )
}

export default ExamMarksheetPrint
